import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type ChangeEvent } from "react";
import { Bot, Sparkles, X, Send, Database, AlertTriangle, Mic, ThumbsUp, ThumbsDown, MessageSquare, Plus, ChevronLeft } from "lucide-react";
import { useLocation } from "react-router-dom";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Message {
  id: string;
  dbId?: number;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  toolCalls?: ToolCallResult[];
  feedbackRating?: "up" | "down" | null;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

interface ToolCallResult {
  name: string;
  label: string;
  data: string;
}

interface PendingAction {
  tool: string;
  params: Record<string, unknown>;
  description: string;
}

const WRITE_TOOLS = new Set(["create_order", "stock_movement"]);

const SLASH_COMMANDS: Record<string, string> = {
  "/庫存": "查詢目前所有庫存狀況",
  "/工單": "查詢所有工單狀態",
  "/設備": "查詢所有設備健康狀態",
  "/價格": "查詢最新原料價格",
  "/配方": "查詢配方相容性",
  "/help": "顯示可用指令列表",
};

const PAGE_CONTEXTS: Record<string, string> = {
  "/inventory": "用戶目前在庫存管理頁面。優先使用 query_inventory 工具回答。",
  "/production": "用戶目前在生管排程頁面。優先使用 query_orders 工具回答。",
  "/maintenance": "用戶目前在設備維護頁面。優先使用 query_equipment 工具回答。",
  "/pricing": "用戶目前在報價頁面。優先使用 query_prices 工具回答。",
  "/recipe-kb": "用戶目前在配方知識庫。優先使用 check_compatibility 工具回答。",
  "/warroom": "用戶目前在戰情室。優先回答設備和異常相關問題。",
  "/bom": "用戶目前在BOM管理頁面。優先回答配方和原料相關問題。",
};

const PAGE_LABELS: Record<string, string> = {
  "/inventory": "庫存管理",
  "/production": "生管排程",
  "/maintenance": "設備維護",
  "/pricing": "報價",
  "/recipe-kb": "配方知識庫",
  "/warroom": "戰情室",
  "/bom": "BOM管理",
};

const MAX_MESSAGES = 50;

const QUICK_ACTIONS = [
  "目前庫存狀況？",
  "新增一張水餃工單",
  "設備健康如何？",
  "豬肉和蝦仁可以一起用嗎？",
];

async function streamChat(
  messages: { role: string; content: string }[],
  context: string | undefined,
  conversationId: string | undefined,
  onChunk: (text: string) => void,
  onToolCall: (tool: { name: string; label: string; result: string }) => void,
  onDone: (data: { conversation_id?: string; message_id?: number }) => void,
) {
  const res = await fetch("/api/ai/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      messages,
      stream: true,
      ...(context ? { context } : {}),
      ...(conversationId ? { conversation_id: conversationId } : {}),
    }),
  });

  if (!res.ok || !res.body) {
    const data = await res.json();
    onChunk(data.response ?? data.error ?? "發生錯誤");
    onDone({});
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        const data = line.slice(6);
        if (data === "[DONE]") {
          onDone({});
          return;
        }
        try {
          const parsed = JSON.parse(data);
          if (parsed.response) {
            onChunk(parsed.response);
          }
          if (parsed.tool_calls) {
            for (const tc of parsed.tool_calls) {
              onToolCall(tc);
            }
          }
          if (parsed.conversation_id) {
            onDone({ conversation_id: parsed.conversation_id, message_id: parsed.message_id });
          }
        } catch {
          onChunk(data);
        }
      }
    }
  }
}

async function fetchConversations(): Promise<Conversation[]> {
  try {
    const res = await fetch("/api/ai/conversations", { credentials: "include" });
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
}

async function fetchConversationMessages(convId: string): Promise<Message[]> {
  try {
    const res = await fetch(`/api/ai/conversations/${convId}/messages`, { credentials: "include" });
    if (!res.ok) return [];
    const rows: { id: number; role: string; content: string; tool_calls_json: string | null; created_at: string }[] = await res.json();
    return rows.map((r) => ({
      id: crypto.randomUUID(),
      dbId: r.id,
      role: r.role as "user" | "assistant",
      content: r.content,
      timestamp: new Date(r.created_at + "Z").getTime(),
      toolCalls: r.tool_calls_json
        ? JSON.parse(r.tool_calls_json).map((name: string) => ({ name, label: name, data: "" }))
        : undefined,
    }));
  } catch {
    return [];
  }
}

async function deleteConversationApi(convId: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/ai/conversations/${convId}`, { method: "DELETE", credentials: "include" });
    return res.ok;
  } catch {
    return false;
  }
}

async function submitFeedback(messageId: number, conversationId: string, rating: "up" | "down") {
  try {
    await fetch("/api/ai/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ message_id: messageId, conversation_id: conversationId, rating }),
    });
  } catch { /* ignore */ }
}

async function fetchMonthCount(): Promise<number> {
  try {
    const res = await fetch("/api/ai/usage/stats", { credentials: "include" });
    if (!res.ok) return 0;
    const data = await res.json();
    return data.month_count ?? 0;
  } catch {
    return 0;
  }
}

async function executeToolCall(tool: string, params: Record<string, unknown>) {
  const res = await fetch("/api/ai/execute", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ tool, params }),
  });
  return res.json();
}

function useSpeechRecognition() {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const isSupported =
    typeof window !== "undefined" &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const startListening = useCallback((onResult: (text: string) => void) => {
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) return;
    const recognition = new Ctor();
    recognition.lang = "zh-TW";
    recognition.continuous = false;
    recognition.onresult = (e) => onResult(e.results[0][0].transcript);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognition.start();
    setIsListening(true);
    recognitionRef.current = recognition;
  }, []);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  return { isListening, startListening, stopListening, isSupported };
}

function formatTime(ts: number) {
  const d = new Date(ts);
  return d.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit" });
}

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 max-w-[80%]">
      <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
        <Bot size={14} className="text-slate-500" />
      </div>
      <div className="rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[bounce_1.4s_ease-in-out_infinite]" />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[bounce_1.4s_ease-in-out_0.2s_infinite]" />
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[bounce_1.4s_ease-in-out_0.4s_infinite]" />
        </div>
      </div>
    </div>
  );
}

function formatToolData(raw: string): string {
  try {
    const data = JSON.parse(raw);
    if (Array.isArray(data) && data.length > 0) {
      const keys = Object.keys(data[0]).filter(k => !k.includes("_at") && k !== "id");
      const header = "| " + keys.join(" | ") + " |";
      const sep = "| " + keys.map(() => "---").join(" | ") + " |";
      const rows = data.slice(0, 8).map((r: Record<string, unknown>) =>
        "| " + keys.map(k => String(r[k] ?? "-")).join(" | ") + " |"
      );
      return [header, sep, ...rows].join("\n") + (data.length > 8 ? `\n\n...共 ${data.length} 筆` : "");
    }
    if (typeof data === "object" && data !== null) {
      return Object.entries(data).map(([k, v]) => `**${k}**: ${v}`).join("\n");
    }
    return raw;
  } catch {
    return raw;
  }
}

function ToolResultCard({ tool }: { tool: ToolCallResult }) {
  const formatted = formatToolData(tool.data);
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 my-2 overflow-x-auto">
      <div className="flex items-center gap-2 mb-2">
        <Database size={14} className="text-blue-500" />
        <span className="text-xs font-semibold text-blue-600">{tool.label}</span>
      </div>
      <div className="text-xs text-slate-700 prose prose-xs max-w-none [&_table]:w-full [&_th]:bg-blue-100 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:text-[11px] [&_td]:px-2 [&_td]:py-1 [&_td]:text-[11px] [&_td]:border-t [&_td]:border-blue-100 [&_strong]:text-slate-800">
        <Markdown remarkPlugins={[remarkGfm]}>{formatted}</Markdown>
      </div>
    </div>
  );
}

function ConfirmActionCard({
  action,
  onConfirm,
  onCancel,
  loading: confirming,
}: {
  action: PendingAction;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 my-2">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={16} className="text-amber-500" />
        <span className="text-sm font-semibold text-amber-700">確認執行操作</span>
      </div>
      <p className="text-sm text-slate-700 mb-3">{action.description}</p>
      <div className="flex items-center gap-2">
        <button
          onClick={onCancel}
          disabled={confirming}
          className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
        >
          取消
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming}
          className="px-3 py-1.5 text-xs rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50"
        >
          {confirming ? "執行中..." : "確認執行"}
        </button>
      </div>
    </div>
  );
}

function SlashCommandDropdown({
  filter,
  onSelect,
}: {
  filter: string;
  onSelect: (cmd: string) => void;
}) {
  const entries = Object.entries(SLASH_COMMANDS).filter(([key]) =>
    key.startsWith(filter),
  );
  if (entries.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-10 max-h-60 overflow-y-auto">
      {entries.slice(0, 6).map(([cmd, desc]) => (
        <button
          key={cmd}
          onClick={() => onSelect(cmd)}
          className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors flex items-center gap-3 border-b border-slate-50 last:border-b-0"
        >
          <span className="text-sm font-mono font-semibold text-qimei-600">{cmd}</span>
          <span className="text-xs text-slate-500">{desc}</span>
        </button>
      ))}
    </div>
  );
}

function MessageBubble({ msg, isStreaming }: { msg: Message; isStreaming?: boolean }) {
  const isUser = msg.role === "user";

  if (isUser) {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-qimei-500 px-4 py-2.5 text-sm text-white whitespace-pre-wrap">
          {msg.content}
        </div>
        <span className="text-[10px] text-slate-400 px-1">{formatTime(msg.timestamp)}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-end gap-2 max-w-[85%]">
        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
          <Bot size={14} className="text-slate-500" />
        </div>
        <div className="min-w-0">
          {msg.toolCalls?.map((tool, i) => (
            <ToolResultCard key={i} tool={tool} />
          ))}
          <div className="rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-2.5 text-sm text-slate-900 prose prose-sm prose-slate max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_strong]:text-slate-800 [&_code]:bg-slate-200 [&_code]:px-1 [&_code]:rounded [&_table]:w-full [&_table]:text-xs [&_table]:border-collapse [&_th]:bg-slate-200 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[11px] [&_td]:px-2 [&_td]:py-1 [&_td]:text-[11px] [&_td]:border-t [&_td]:border-slate-200 [&_tr:hover]:bg-slate-50 overflow-x-auto transition-[min-height] duration-150 ease-out">
            <Markdown remarkPlugins={[remarkGfm]}>{msg.content}</Markdown>
            {isStreaming && <span className="inline-block ml-0.5 animate-[blink_0.8s_step-end_infinite] text-slate-400">▌</span>}
          </div>
        </div>
      </div>
      {!isStreaming && <span className="text-[10px] text-slate-400 pl-9">{formatTime(msg.timestamp)}</span>}
    </div>
  );
}

function FeedbackButtons({
  msg,
  conversationId,
  onFeedback,
}: {
  msg: Message;
  conversationId: string | null;
  onFeedback: (msgId: string, rating: "up" | "down") => void;
}) {
  if (msg.role !== "assistant" || !msg.dbId || !conversationId) return null;

  return (
    <div className="flex items-center gap-1 pl-9 -mt-1">
      <button
        onClick={() => onFeedback(msg.id, "up")}
        className={`p-1 rounded hover:bg-slate-100 transition-colors ${msg.feedbackRating === "up" ? "text-emerald-500" : "text-slate-300 hover:text-slate-500"}`}
      >
        <ThumbsUp size={12} />
      </button>
      <button
        onClick={() => onFeedback(msg.id, "down")}
        className={`p-1 rounded hover:bg-slate-100 transition-colors ${msg.feedbackRating === "down" ? "text-red-400" : "text-slate-300 hover:text-slate-500"}`}
      >
        <ThumbsDown size={12} />
      </button>
    </div>
  );
}

function ConversationSidebar({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  onClose,
}: {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col animate-[slide-in_150ms_ease-out_both]">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 transition-colors">
          <ChevronLeft size={16} className="text-slate-500" />
        </button>
        <span className="text-sm font-medium text-slate-700 flex-1">對話記錄</span>
        <button
          onClick={onNew}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-qimei-600 hover:bg-qimei-50 transition-colors"
        >
          <Plus size={12} />
          新對話
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-8">尚無對話記錄</div>
        )}
        {conversations.map((conv) => (
          <div
            key={conv.id}
            className={`group flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-50 ${activeId === conv.id ? "bg-qimei-50" : ""}`}
            onClick={() => onSelect(conv.id)}
          >
            <MessageSquare size={14} className="text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-700 truncate">{conv.title || "新對話"}</div>
              <div className="text-[10px] text-slate-400">{new Date(conv.updated_at + "Z").toLocaleDateString("zh-TW")}</div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(conv.id); }}
              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 transition-all"
            >
              <X size={12} className="text-slate-400" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AiChat() {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [monthCount, setMonthCount] = useState(0);
  const [streaming, setStreaming] = useState(false);
  const streamingMsgIdRef = useRef<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const location = useLocation();
  const pageContext = PAGE_CONTEXTS[location.pathname] ?? "";
  const pageLabel = PAGE_LABELS[location.pathname];
  const { isListening, startListening, stopListening, isSupported: speechSupported } = useSpeechRecognition();

  useEffect(() => {
    const t = setTimeout(() => setShowPulse(false), 6000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, streaming]);

  useEffect(() => {
    if (expanded) {
      setTimeout(() => inputRef.current?.focus(), 200);
      fetchConversations().then(setConversations);
      fetchMonthCount().then(setMonthCount);
    }
  }, [expanded]);

  const handleConfirm = useCallback(async () => {
    if (!pendingAction) return;
    setConfirmLoading(true);
    try {
      const result = await executeToolCall(pendingAction.tool, pendingAction.params);
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: result.message ?? `已執行：${pendingAction.description}`,
          timestamp: Date.now(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "操作執行失敗，請稍後再試。",
          timestamp: Date.now(),
        },
      ]);
    } finally {
      setPendingAction(null);
      setConfirmLoading(false);
    }
  }, [pendingAction]);

  const handleCancelAction = useCallback(() => {
    setPendingAction(null);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "已取消操作。",
        timestamp: Date.now(),
      },
    ]);
  }, []);

  const handleNewConversation = useCallback(() => {
    setConversationId(null);
    setMessages([]);
    setShowSidebar(false);
  }, []);

  const handleSelectConversation = useCallback(async (id: string) => {
    setConversationId(id);
    setShowSidebar(false);
    const msgs = await fetchConversationMessages(id);
    setMessages(msgs);
  }, []);

  const handleDeleteConversation = useCallback(async (id: string) => {
    await deleteConversationApi(id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (conversationId === id) {
      setConversationId(null);
      setMessages([]);
    }
  }, [conversationId]);

  const handleFeedback = useCallback(
    (msgId: string, rating: "up" | "down") => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== msgId) return m;
          const newRating = m.feedbackRating === rating ? null : rating;
          if (m.dbId && conversationId && newRating) {
            submitFeedback(m.dbId, conversationId, newRating);
          }
          return { ...m, feedbackRating: newRating };
        }),
      );
    },
    [conversationId],
  );

  const doSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;

      if (trimmed === "/help") {
        setInput("");
        const helpLines = Object.entries(SLASH_COMMANDS)
          .map(([cmd, desc]) => `**${cmd}** — ${desc}`)
          .join("\n");
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "user", content: trimmed, timestamp: Date.now() },
          { id: crypto.randomUUID(), role: "assistant", content: `可用指令：\n${helpLines}`, timestamp: Date.now() },
        ]);
        return;
      }

      const slashText = SLASH_COMMANDS[trimmed];
      const actualText = slashText ?? trimmed;

      const userMsg: Message = {
        id: crypto.randomUUID(),
        role: "user",
        content: actualText,
        timestamp: Date.now(),
      };

      setMessages((prev) => {
        const next = [...prev, userMsg];
        return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next;
      });
      setInput("");
      setShowSlashMenu(false);
      setLoading(true);

      const assistantMsgId = crypto.randomUUID();
      const assistantMsg: Message = {
        id: assistantMsgId,
        role: "assistant",
        content: "",
        timestamp: Date.now(),
      };
      setMessages((prev) => {
        const next = [...prev, assistantMsg];
        return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next;
      });
      setStreaming(true);
      streamingMsgIdRef.current = assistantMsgId;

      try {
        const apiMessages = [...messages, userMsg].slice(-20).map((m) => ({
          role: m.role,
          content: m.content,
        }));

        let receivedText = "";

        await streamChat(
          apiMessages,
          pageContext || undefined,
          conversationId ?? undefined,
          (chunk) => {
            receivedText += chunk;
            const updatedText = receivedText;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, content: updatedText.replace(/^\s+/, "") }
                  : m,
              ),
            );
          },
          (tool) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? {
                      ...m,
                      toolCalls: [
                        ...(m.toolCalls ?? []),
                        { name: tool.name, label: tool.label, data: tool.result },
                      ],
                    }
                  : m,
              ),
            );

            if (WRITE_TOOLS.has(tool.name)) {
              setPendingAction({
                tool: tool.name,
                params: {},
                description: tool.label ?? tool.name,
              });
            }
          },
          (doneData) => {
            if (doneData.conversation_id) {
              if (!conversationId) {
                setConversationId(doneData.conversation_id);
                fetchConversations().then(setConversations);
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, dbId: doneData.message_id }
                    : m,
                ),
              );
            }
          },
        );

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId && !m.content
              ? { ...m, content: "抱歉，我暫時無法回應。" }
              : m,
          ),
        );

        setMonthCount((c) => c + 1);
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: "網路連線異常，請稍後再試。" }
              : m,
          ),
        );
      } finally {
        setStreaming(false);
        streamingMsgIdRef.current = null;
        setLoading(false);
      }
    },
    [loading, messages, pageContext, conversationId],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      doSend(input);
    }
    if (e.key === "Escape" && showSlashMenu) {
      setShowSlashMenu(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    setShowSlashMenu(val.startsWith("/") && val.length >= 1);
  };

  const handleSlashSelect = (cmd: string) => {
    setShowSlashMenu(false);
    setInput("");
    doSend(cmd);
  };

  const handleMicClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening((text) => {
        setInput((prev) => prev + text);
      });
    }
  };

  const showQuickActions = messages.length === 0 && !loading;

  return (
    <>
      {/* FAB button */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="fixed right-6 bottom-6 z-50 w-14 h-14 rounded-full bg-qimei-500 text-white shadow-lg hover:bg-qimei-600 transition-all duration-200 flex items-center justify-center hover:scale-105 active:scale-95"
        >
          {showPulse && (
            <span className="absolute inset-0 rounded-full bg-qimei-500 animate-ping opacity-40" />
          )}
          <Bot size={24} className="relative" />
        </button>
      )}

      {/* Chat window */}
      {expanded && (
        <div
          className="fixed right-6 bottom-6 z-50 w-[380px] h-[520px] max-sm:right-0 max-sm:bottom-0 max-sm:w-full max-sm:h-full max-sm:rounded-none rounded-2xl shadow-2xl bg-white flex flex-col overflow-hidden animate-[scale-in_200ms_cubic-bezier(0.22,1,0.36,1)_both]"
        >
          {/* Sidebar overlay */}
          {showSidebar && (
            <ConversationSidebar
              conversations={conversations}
              activeId={conversationId}
              onSelect={handleSelectConversation}
              onNew={handleNewConversation}
              onDelete={handleDeleteConversation}
              onClose={() => setShowSidebar(false)}
            />
          )}

          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-100 bg-white shrink-0">
            <button
              onClick={() => setShowSidebar(true)}
              className="w-8 h-8 rounded-full bg-qimei-500 flex items-center justify-center hover:bg-qimei-600 transition-colors"
              title="對話記錄"
            >
              <Bot size={16} className="text-white" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-slate-800">小奇 AI 助理</span>
                <Sparkles size={13} className="text-amber-400" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-[10px] text-slate-400">線上</span>
                {pageLabel && (
                  <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-qimei-50 text-qimei-600 font-medium">
                    {pageLabel}
                  </span>
                )}
                {monthCount > 0 && (
                  <span className="text-[10px] text-slate-300 ml-1">本月 {monthCount} 次查詢</span>
                )}
              </div>
            </div>
            <button
              onClick={handleNewConversation}
              className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
              title="新對話"
            >
              <Plus size={16} className="text-slate-400" />
            </button>
            <button
              onClick={() => setExpanded(false)}
              className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
            >
              <X size={16} className="text-slate-400" />
            </button>
          </div>

          {/* Messages */}
          <div className="relative flex-1 overflow-y-auto px-4 py-3 space-y-4 scroll-smooth">
            {messages.length === 0 && !loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <img src="/assets/chat-assistant-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
                <div className="relative text-center space-y-2 px-6">
                  <div className="w-12 h-12 rounded-full bg-qimei-50 flex items-center justify-center mx-auto">
                    <Bot size={22} className="text-qimei-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">嗨！我是小奇</p>
                  <p className="text-xs text-slate-400">有什麼可以幫您的嗎？輸入問題或使用 / 指令開始對話</p>
                </div>
              </div>
            )}
            {messages.map((msg) => {
              const isMsgStreaming = streaming && msg.id === streamingMsgIdRef.current;
              if (isMsgStreaming && !msg.content && !msg.toolCalls?.length) {
                return <TypingIndicator key={msg.id} />;
              }
              return (
                <div key={msg.id}>
                  <MessageBubble msg={msg} isStreaming={isMsgStreaming} />
                  {!isMsgStreaming && (
                    <FeedbackButtons
                      msg={msg}
                      conversationId={conversationId}
                      onFeedback={handleFeedback}
                    />
                  )}
                </div>
              );
            })}
            {pendingAction && (
              <ConfirmActionCard
                action={pendingAction}
                onConfirm={handleConfirm}
                onCancel={handleCancelAction}
                loading={confirmLoading}
              />
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick actions */}
          {showQuickActions && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0">
              {QUICK_ACTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => doSend(q)}
                  className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 pb-3 pt-2 border-t border-slate-100 shrink-0">
            {isListening && (
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-red-500 font-medium">聆聽中...</span>
              </div>
            )}
            <div className="relative flex items-end gap-2">
              {showSlashMenu && (
                <SlashCommandDropdown filter={input} onSelect={handleSlashSelect} />
              )}
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="問我任何問題... 輸入 / 查看指令"
                disabled={loading}
                rows={1}
                className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors focus:border-qimei-300 focus:bg-white focus:ring-2 focus:ring-qimei-100 disabled:opacity-50 max-h-24 overflow-y-auto"
                style={{ minHeight: "40px" }}
              />
              {speechSupported && (
                <button
                  onClick={handleMicClick}
                  className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center transition-colors ${
                    isListening
                      ? "bg-red-500 text-white animate-pulse hover:bg-red-600"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  <Mic size={16} />
                </button>
              )}
              <button
                onClick={() => doSend(input)}
                disabled={loading || !input.trim()}
                className="w-9 h-9 shrink-0 rounded-xl bg-qimei-500 text-white flex items-center justify-center hover:bg-qimei-600 transition-colors disabled:opacity-40 disabled:hover:bg-qimei-500"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
